import { AsyncPipe, CommonModule, NgForOf, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal, computed, OnDestroy, OnInit, ChangeDetectorRef, inject, ViewChild, ElementRef } from '@angular/core';
import type { ValidatorFn } from '@angular/forms';
import { FormControl, FormsModule, ReactiveFormsModule, FormGroup, FormArray, Validators } from '@angular/forms';
import { RouterLink, ActivatedRoute } from '@angular/router';
import type { TuiBooleanHandler } from '@taiga-ui/cdk';
import { TuiDay, TuiLet, TuiRepeatTimesPipe, TuiTime, TuiContext, TuiIdentityMatcher, TuiStringHandler, TuiDayRange, TuiPlatform, tuiDefaultSort, TuiValidator } from '@taiga-ui/cdk';
import { TuiAppearance, TuiButton, TuiInitialsPipe, TuiTitle, TuiLink, TuiPopup, TuiIconPipe, TuiScrollbar, TuiError, TuiGroup, TuiLabel, TuiTextfield, TuiDataList, TuiNotification, tuiDateFormatProvider, TuiIcon, TuiFormatNumberPipe, TuiNumberFormat, TuiFallbackSrcPipe, TuiFormatDatePipe, TuiDropdown, TuiHintDirective } from '@taiga-ui/core';
import type { TuiSizeS, tuiLoaderOptionsProvider } from '@taiga-ui/core';
import type { TuiPaymentSystem } from '@taiga-ui/addon-commerce';
import { TuiThumbnailCard, TuiCurrencyPipe, TuiCurrency } from '@taiga-ui/addon-commerce';
import type { TuiComparator } from '@taiga-ui/addon-table';
import { TuiTable } from '@taiga-ui/addon-table';
import { WaIntersectionObserver } from '@ng-web-apis/intersection-observer';
import { TuiBlockStatus } from '@taiga-ui/layout';
import { TuiResponsiveDialogService } from '@taiga-ui/addon-mobile';
import { TUI_CONFIRM, TUI_VALIDATION_ERRORS, TuiConfirmData, TuiSkeleton } from '@taiga-ui/kit';
import { switchMap, interval, skip, distinctUntilChanged, filter, map, catchError, forkJoin } from 'rxjs';
import { ApiService } from '../../../shared/services/api.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { RoleService } from '../../../shared/services/role.service';

import {
  TuiAvatar,
  TuiBadge,
  TuiBadgedContent,
  TuiCheckbox,
  TuiChevron,
  TuiDataListWrapper,
  TuiFilterByInputPipe,
  TuiFieldErrorPipe,
  TuiInputNumber,
  TuiPassword,
  TuiRadio,
  TuiSelect,
  TuiSortCountriesPipe,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTooltip,
  tuiInputPhoneInternationalOptionsProvider,
  TuiDrawer,
  TuiTabs,
  TuiBlock,
  TuiInputInline,
  TuiTextarea,
  TuiChip,
  TuiStatus,
  TuiPagination,
  TuiDataListDropdownManager,
} from '@taiga-ui/kit';
import { TuiCardLarge, TuiForm, TuiHeader, TuiNavigation, TuiCell } from '@taiga-ui/layout';
import { TuiSelectModule, TuiInputDateModule, TuiInputModule, TuiInputPhoneModule, TuiInputSliderModule, TuiInputTimeModule, TuiTextfieldControllerModule, TuiMultiSelectModule, TuiInputDateRangeModule, TuiTextareaModule } from '@taiga-ui/legacy';
import { delay, of, defer, Subscription, Observable } from 'rxjs';
import type { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { getCountries } from 'libphonenumber-js';
import { CurrencyService } from '../../../shared/services/currency.service';
import { VehicleIconsService } from '../../../shared/services/vehicle-icons.service';
import { DrawerInviteTeamMember } from '../../../shared/components/drawers/drawer-invite-team-member/drawer-invite-team-member';
import { DevicesDrawer, DeviceData } from '../../../shared/components/drawers/devices-drawer/devices-drawer';
import { PermissionDisableDirective } from '../../../shared/directives/permission-disable.directive';
import { FormatUTCToOrgTimezonePipe } from '../../../shared/pipes/timezone.pipe';
import { UserInitialsPipe } from '../../../shared/pipes/fast-rendering.pipes';

// API Response Interface
interface ApiTeamMember {
  id: string;
  email: string;
  firstName: string;
  profile: string | null;
  lastName: string;
  password: string;
  phone: string | null;
  forget_otp: string | null;
  gender: string | null;
  type: string | null;
  role_id: string;
  org_id: string;
  timeStamp: string | null;
  language: string | null;
  point: any;
  parkingId: string | null;
  vehicleId: string | null;
  status: boolean;
  adminId: string | null;
  company: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  user_pov?: string | null;
}

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  role_id?: string;
  lastLogin: Date;
  devices: number;
  status: string;
  actions: string;
  isActive: boolean;
  avatar?: string;
  initials?: string;
  parkingId?: string | null;
  parkingIds?: string[];
  scopeId?: string;
  scopeType?: 'organization' | 'parking';
  assignedParkingIds?: string[];
  parkingName?: string | null;
  type?: string | null;
  phone?: string;
  gender?: string;
  service_access?: Array<{
    vehicle: boolean;
    passes: boolean;
    facility: string[];
  }>;
  user_pov?: string | null;
}

interface TeamRole {
  name: string;
  description: string;
}

@Component({
  standalone: true,
  exportAs: "settings",
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TuiAppearance,
    TuiAvatar,
    TuiBadge,
    TuiButton,
    TuiChevron,
    TuiForm,
    TuiHeader,
    TuiNavigation,
    TuiDataListWrapper,
    TuiSelectModule,
    TuiCardLarge,
    TuiSwitch,
    TuiBadgedContent,
    TuiInputNumber,
    TuiRadio,
    TuiSelect,
    TuiCell,
    TuiTitle,
    TuiTextfield,
    TuiDataList,
    TuiTabs,
    TuiInputDateModule,
    TuiInputModule,
    TuiInputPhoneModule,
    TuiInputSliderModule,
    TuiInputTimeModule,
    TuiTextfieldControllerModule,
    TuiMultiSelectModule,
    TuiInputDateRangeModule,
    TuiTextareaModule,
    TuiTable,
    WaIntersectionObserver,
    TuiIcon,
    TuiStatus,
    TuiBlockStatus,
    TuiChip,
    TuiPagination,
    TuiHintDirective,
    DrawerInviteTeamMember,
    DevicesDrawer,
    FormatUTCToOrgTimezonePipe,
    UserInitialsPipe,
    PermissionDisableDirective,
    TuiDropdown,
    TuiDataListDropdownManager,
    TuiSkeleton,
    TuiRepeatTimesPipe,
    TuiFieldErrorPipe,
    TuiError,
    TuiPassword,
    TuiLabel
  ],
  templateUrl: './team.html',
  styleUrl: './team.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: TUI_VALIDATION_ERRORS,
      useValue: {
        required: 'New Password is required',
        minlength: ({ requiredLength }: { requiredLength: number }) =>
          `Password must be at least ${requiredLength} characters`,
      },
    },
  ],
})
export default class Team implements OnInit {
  constructor() {
    console.log('️ Team component constructor called');
  }

  private readonly apiService = inject(ApiService);
  private readonly notificationService = inject(NotificationService);
  protected readonly roleService = inject(RoleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly dialogs = inject(TuiResponsiveDialogService);
  private subscription = new Subscription();
  protected skeleton = true;
  protected readonly roleDisplayTextSignal = computed(() => this.getRoleDisplayText());

  // Track current user for authentication changes
  private currentUserId: string | null = null;
  private currentOrgId: string | null = null;

  // Track current context IDs to avoid redundant subscriptions
  protected contextParkingId: string | null = null;
  protected contextOrgId: string | null = null;

  protected readonly form = new FormGroup({
    search: new FormControl(''),
    statusFilter: new FormControl(''),
    roleFilter: new FormControl(''),
    date: new FormControl(),
    switch: new FormControl(),
    filter: new FormControl(),
    segmented: new FormControl(),
  });

  @ViewChild('changePasswordDialog')
  protected changePasswordDialog?: any;

  protected selectedMemberForPassword: TeamMember | null = null;
  protected readonly changePasswordForm = new FormGroup({
    newPassword: new FormControl('', [Validators.required, Validators.minLength(6)])
  });


  // Add missing form for vehicle selection
  protected readonly vehicleSelectionForm = new FormGroup({
    selectedCard: new FormControl(null)
  });

  // Add missing properties
  protected selectedCard: TeamRole | null = null;
  protected readonly stringify: TuiStringHandler<TeamRole> = (item) => item.name;

  // Filter options
  protected readonly statusOptions = ['Active', 'Inactive', 'Suspended', 'Pending'];
  protected readonly roleOptions = ['Admin', 'User', 'Manager', 'Viewer', 'Operator', 'Accountant'];
  protected readonly deviceColumns = ['device', 'location', 'first-sign-in', 'last-activity', 'action'] as const;

  // Applied filters (separate from form to control when filters are actually applied)
  protected appliedFilters = {
    search: '',
    status: '',
    role: ''
  };

  // Store calculated dropdown widths
  protected dropdownWidths: { [key: string]: string } = {};

  // API data
  protected readonly data = signal<TeamMember[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected orgParkings: any[] = [];
  private orgUsersCacheMap = new Map<string, any>();

  // Subscription / custom_limits tracking
  protected readonly parkingSubscription = signal<any | null>(null);

  /** Returns true only when viewing a specific parking lot context (Parking POV) */
  protected get isParkingPOV(): boolean {
    return Boolean(this.contextParkingId);
  }

  /** max_user from custom_limits (0 or null = unlimited). Only active in Parking POV */
  private get maxUsersFromSubscription(): number | null {
    if (!this.isParkingPOV) return null; // Org POV has no user cap limit
    const sub = this.parkingSubscription();
    if (!sub) return null;
    const val =
      sub?.custom_limits?.max_user ??
      sub?.plan?.max_user ??
      sub?.max_user ??
      sub?.max_users ??
      sub?.parking_details?.custom_limits?.max_user ??
      null;
    if (val === null || val === undefined) return null;
    const n = Number(val);
    return isNaN(n) ? null : n;
  }

  /** True when in Parking POV, a finite cap exists and current users >= cap */
  protected get isUserLimitReached(): boolean {
    if (!this.isParkingPOV) return false; // Org POV has no limit restriction
    const max = this.maxUsersFromSubscription;
    if (!max || max <= 0) return false;
    return this.data().length >= max;
  }

  private _index = 0;
  protected get index(): number {
    return this._index;
  }
  protected set index(val: number) {
    if (this._index !== val) {
      this._index = val;
      this.enrichVisiblePageDeviceCounts();
    }
  }
  protected length = 10;
  protected size = 25;
  protected readonly pageSizeOptions = [25, 50, 100];
  protected isPageSizeDropdownOpen = false;
  protected readonly items = ['Active', 'Inactive', 'Suspended', 'Pending'];
  protected readonly itemsRole = ['Admin', 'User', 'Manager', 'Viewer', 'Operator', 'Accountant'];
  protected readonly content: TuiStringHandler<TuiContext<string>> = ({ $implicit }) =>
    `${$implicit}`;

  // Filter properties
  protected readonly filters = ['Admin', 'User', 'Manager', 'Viewer', 'Operator', 'Accountant'];
  protected readonly segments = [null, 'Active', 'Inactive', 'Suspended', 'Pending'];

  // Add currentUser placeholder to resolve linter error
  protected currentUser = {
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // Team roles
  protected readonly teamRoles = [
    {
      name: 'Admin',
      description: 'Full access to the entire organization - can manage parking lots, users, settings, billing, and create custom roles. Ideal for business owners or IT leads.'
    },
    {
      name: 'Manager',
      description: 'Manages one or more parking lots. Can view reports, configure facilities and pricing, and oversee operators. Best suited for local managers or site-level supervisors.'
    },
    {
      name: 'Vendor',
      description: 'Manages one or more parking lots. Can view reports, configure facilities and pricing, and oversee operators. Best suited for local vendors or site-level supervisors.'
    },
    {
      name: 'Operator',
      description: 'Day-to-day parking operator. Can log vehicle entries/exits, apply passes, and manage facility usage like valet or EV charging. No access to settings or billing.'
    },
    {
      name: 'Accountant',
      description: 'Can view and download transactions, monitor revenue reports, and track financial performance. No access to parking logs or team management.'
    },
  ];




  // Dropdown state management
  protected openDropdowns = new Set<string>();

  // Filter count signal
  protected readonly count = signal(0);

  protected updateFilterCount() {
    const activeFilters = Object.values(this.appliedFilters).filter(value => value !== '').length;
    this.count.set(activeFilters);
  }

  protected onFormValueChange() {
    // This will be called when form values change, but filters won't be applied until Apply button is clicked
  }

  ngOnInit() {
    console.log('Team component ngOnInit called');
    console.log('Current route:', window.location.href);

    this.initializeDropdownWidths();

    // Subscribe to route parameters once on initialization
    this.subscription.add(
      this.route.params.subscribe(params => {
        console.log('Route params updated:', params);
        this.updateContextFromParams(params);
        this.loadTeamMembers();
        this.loadParkingSubscriptionForContext();
      })
    );

    this.setupAuthenticationMonitoring();

    // Safety timeout to turn off skeleton after 10 seconds
    setTimeout(() => {
      this.skeleton = false;
      this.cdr.detectChanges();
    }, 10000);
  }

  /** Fetch active subscription for the current parking context to read custom_limits */
  private loadParkingSubscriptionForContext(): void {
    const parkingId = this.contextParkingId || this.apiService.getCurrentParkingId();
    const orgId = this.contextOrgId || this.apiService.getCurrentOrganizationId();

    if (!parkingId && !orgId) {
      this.parkingSubscription.set(null);
      return;
    }

    const processSubResponse = (response: any): boolean => {
      const subs: any[] = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : response ? [response] : [];
      const active = subs.find((s: any) => s?.status === true || s?.status === 'active') ?? subs[0] ?? null;
      if (active) {
        this.parkingSubscription.set(active);
        this.cdr.detectChanges();
        return true;
      }
      return false;
    };

    if (parkingId) {
      this.subscription.add(
        this.apiService.getActiveSubscriptionsByParkingId(parkingId).subscribe({
          next: (response: any) => {
            if (!processSubResponse(response)) {
              this.fetchParkingDetailSubscriptionFallback(parkingId, orgId);
            }
          },
          error: () => {
            this.fetchParkingDetailSubscriptionFallback(parkingId, orgId);
          }
        })
      );
    } else if (orgId) {
      this.fetchOrgSubscriptionFallback(orgId);
    }
  }

  private fetchParkingDetailSubscriptionFallback(parkingId: string, orgId?: string | null): void {
    this.apiService.getParkingDetail(parkingId).subscribe({
      next: (res: any) => {
        const pData = res?.data || res;
        if (pData?.subscription) {
          this.parkingSubscription.set(pData.subscription);
          this.cdr.detectChanges();
        } else if (pData?.subscription_id) {
          this.apiService.getSubscriptionById(pData.subscription_id).subscribe({
            next: (subRes: any) => {
              const sub = subRes?.data || subRes;
              if (sub) {
                this.parkingSubscription.set(sub);
                this.cdr.detectChanges();
              } else if (orgId) {
                this.fetchOrgSubscriptionFallback(orgId);
              }
            },
            error: () => {
              if (orgId) this.fetchOrgSubscriptionFallback(orgId);
            }
          });
        } else if (orgId) {
          this.fetchOrgSubscriptionFallback(orgId);
        }
      },
      error: () => {
        if (orgId) this.fetchOrgSubscriptionFallback(orgId);
      }
    });
  }

  private fetchOrgSubscriptionFallback(orgId: string): void {
    this.apiService.getActiveSubscriptionsByOrgId(orgId).subscribe({
      next: (response: any) => {
        const subs: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response ? [response] : [];
        const active = subs.find((s: any) => s?.status === true || s?.status === 'active') ?? subs[0] ?? null;
        if (active) {
          this.parkingSubscription.set(active);
          this.cdr.detectChanges();
        }
      }
    });
  }

  private initializeDropdownWidths() {
    this.dropdownWidths = {
      status: this.getDropdownWidth(this.statusOptions),
      role: this.getDropdownWidth(this.roleOptions)
    };
  }

  private updateContextFromParams(params: any): void {
    let parkingId: string | null = null;
    let orgId: string | null = null;
    const currentOrgId = this.apiService.getCurrentOrganizationId();

    // Collect all parameters from the current route up to the root
    const allParams: any = { ...params };
    let current: ActivatedRoute | null = this.route;
    while (current) {
      Object.assign(allParams, current.snapshot.params);
      current = current.parent;
    }

    console.log('Detected Context Params:', allParams);

    // Identify the ID - look for 'parkingId' first then 'id' fallback
    const routeId = allParams['parkingId'] || allParams['id'];

    if (routeId && routeId.includes('-')) {
      // Check if the route ID matches the current organization ID
      // If it matches, treat it as org ID, otherwise treat as parking ID
      if (currentOrgId && routeId === currentOrgId) {
        orgId = routeId;
        console.log('Organization ID from route params:', orgId);
        if (orgId) sessionStorage.setItem('organization_id', orgId);
      } else {
        parkingId = routeId;
        console.log('️ Parking ID from route params:', parkingId);
        if (parkingId) sessionStorage.setItem('parking_id', parkingId);
        // Also set org ID for fallback
        orgId = currentOrgId;
        if (orgId) sessionStorage.setItem('organization_id', orgId);
      }
    } else if (allParams['orgId'] && allParams['orgId'].includes('-')) {
      orgId = allParams['orgId'];
      console.log('Organization ID from route params (orgId):', orgId);
      if (orgId) sessionStorage.setItem('organization_id', orgId);
    } else {
      // Fall back to API service
      orgId = currentOrgId;
      console.log('Organization ID from API service:', orgId);
    }

    this.contextParkingId = parkingId;
    this.contextOrgId = orgId;
  }

  protected loadTeamMembers() {
    console.log('loadTeamMembers triggered', { parkingId: this.contextParkingId, orgId: this.contextOrgId });

    // Prevent concurrent loads if needed, but for now just clear old data
    this.skeleton = true;
    this.loading.set(true);
    this.error.set(null);

    // Load org parkings for "all parkings" access display
    const orgId = this.contextOrgId || this.apiService.getCurrentOrganizationId();
    if (orgId) {
      this.apiService.getParkingListByOrg(orgId).subscribe({
        next: (res: any) => {
          if (Array.isArray(res)) {
            this.orgParkings = res;
          } else if (res?.data && Array.isArray(res.data)) {
            this.orgParkings = res.data;
          } else if (res?.parkings && Array.isArray(res.parkings)) {
            this.orgParkings = res.parkings;
          }
          this.cdr.detectChanges();
        },
        error: () => { /* silently fallback to getUserParkings */ }
      });

      // Also cache org users so we have complete parkings access lists for members loaded in parking context
      this.apiService.getAllOrganizationUsers(orgId).subscribe({
        next: (res: any) => {
          let users: any[] = [];
          if (Array.isArray(res)) users = res;
          else if (res?.users && Array.isArray(res.users)) users = res.users;
          else if (res?.data && Array.isArray(res.data)) users = res.data;

          users.forEach((u: any) => {
            if (u.id) this.orgUsersCacheMap.set(u.id, u);
            if (u.email) this.orgUsersCacheMap.set(u.email.toLowerCase(), u);
          });

          // If team members are already rendered, enrich their parkingIds now
          const currentMembers = this.data();
          if (currentMembers && currentMembers.length > 0) {
            let updated = false;
            currentMembers.forEach(m => {
              const cached = this.orgUsersCacheMap.get(m.id) || (m.email ? this.orgUsersCacheMap.get(m.email.toLowerCase()) : null);
              if (cached) {
                let cachedIds: string[] = [];
                if (cached.parkingIds && Array.isArray(cached.parkingIds)) {
                  cachedIds = cached.parkingIds;
                } else if (cached.parkings && Array.isArray(cached.parkings)) {
                  cachedIds = cached.parkings.filter((p: any) => p != null).map((p: any) => p.id);
                }
                if (cachedIds.length > 0 && JSON.stringify(m.parkingIds) !== JSON.stringify(cachedIds)) {
                  m.parkingIds = cachedIds;
                  if (!m.parkingId && cachedIds.length > 0) m.parkingId = cachedIds[0];
                  updated = true;
                }
              }
            });
            if (updated) {
              this.data.set([...currentMembers]);
              this.filteredData.set([...this.filteredData()]);
              this.cdr.detectChanges();
            }
          }
        },
        error: () => {}
      });
    }

    // If we have a parking ID, use it directly
    if (this.contextParkingId) {
      this.loadTeamMembersWithParkingId(this.contextParkingId);
    } else if (this.contextOrgId) {
      // Otherwise use organization ID
      this.loadTeamMembersWithOrgId(this.contextOrgId);
    } else {
      console.error('Neither Parking ID nor Organization ID found');
      this.error.set('ID not found');
      this.loading.set(false);
      this.skeleton = false;
      this.cdr.detectChanges();
    }
  }

  private loadTeamMembersWithOrgId(orgId: string) {
    console.log('Loading team members for organization:', orgId);

    // First load roles, then load team members
    this.subscription.add(
      this.roleService.getRoles().subscribe({
        next: (roles) => {
          // Use organization-wide API with a higher limit to fetch all members
          console.log('Using organization-wide API with org ID:', orgId);
          const apiCall = this.apiService.getAllOrganizationUsers(orgId);

          // Now load team members using the appropriate API
          this.subscription.add(
            apiCall.pipe(
              switchMap((response: any) => {
                // Fetch organization detail to get the owner/admin
                return this.apiService.getOrganization(orgId).pipe(
                  map(orgDetail => ({ response, orgDetail })),
                  catchError(() => of({ response, orgDetail: null }))
                );
              })
            ).subscribe({
              next: ({ response, orgDetail }: any) => {
                // Handle different response formats
                let teamData: any[] = [];

                if (Array.isArray(response)) {
                  teamData = response;
                } else if (response && response.users && Array.isArray(response.users)) {
                  teamData = response.users;
                } else if (response && response.data && Array.isArray(response.data)) {
                  teamData = response.data;
                } else if (response && response.success && response.data && Array.isArray(response.data)) {
                  teamData = response.data;
                }

                // If orgDetail exists and has a user (owner), add them if not already in teamData
                if (orgDetail && orgDetail.user) {
                  const owner = orgDetail.user;
                  const ownerId = owner.id || owner.user_id;
                  const alreadyInList = teamData.some(m => m.id === ownerId || m.email?.toLowerCase() === owner.email?.toLowerCase());

                  if (!alreadyInList) {
                    console.log('Adding organization owner to team list:', owner.email);
                    // Ensure owner has required fields for mapping
                    const mappedOwner = {
                      ...owner,
                      id: ownerId,
                      type: 'admin',
                      status: 'active',
                      role: { name: 'Admin' }
                    };
                    teamData.unshift(mappedOwner); // Put owner at the top
                  }
                }

                console.log('Team data received:', teamData.length, 'members');

                if (teamData.length > 0) {
                  // For organization-wide view, we show all members without filtering by parking context
                  const teamMembers = teamData.map((member: ApiTeamMember) => this.mapApiMemberToTeamMember(member));

                  this.data.set(teamMembers);
                  this.filteredData.set(teamMembers);
                  // Enrich team members with device counts from our sessions API
                  this.enrichVisiblePageDeviceCounts();
                  // this.fetchParkingNames(teamMembers);
                } else {
                  this.data.set([]);
                  this.filteredData.set([]);
                }

                this.loading.set(false);
                this.skeleton = false;
                this.cdr.detectChanges();
              },
              error: (error: any) => {
                console.error('Error loading team members:', error);
                this.error.set('Failed to load team members');
                this.loading.set(false);
                this.skeleton = false;
                this.cdr.detectChanges();
              }
            })
          );
        },
        error: (error) => {
          // Continue with team loading even if roles fail
          this.loadTeamMembersWithoutRoles(orgId);
        }
      })
    );
  }

  private loadTeamMembersWithParkingId(parkingId: string) {
    console.log('Loading team members for parking:', parkingId);
    this.loading.set(true);
    this.error.set(null);

    // First load roles, then load team members
    this.subscription.add(
      this.roleService.getRoles().subscribe({
        next: (roles) => {
          // Use parking-specific API
          this.subscription.add(
            this.apiService.getParkingUsers(parkingId).subscribe({
              next: (response: any) => {
                // Handle different response formats
                let teamData: any[] = [];

                if (Array.isArray(response)) {
                  // Direct array response
                  teamData = response;
                } else if (response && response.users && Array.isArray(response.users)) {
                  // Response with users array (parking users API format)
                  teamData = response.users;
                } else if (response && response.data && Array.isArray(response.data)) {
                  // Wrapped in ApiResponse object
                  teamData = response.data;
                } else if (response && response.success && response.data && Array.isArray(response.data)) {
                  // Standard ApiResponse format
                  teamData = response.data;
                } else {
                  console.warn('️ Unexpected response format:', response);
                  this.error.set('Invalid response format');
                  this.loading.set(false);
                  this.skeleton = false;
                  this.cdr.detectChanges();
                  return;
                }

                console.log('Team data received:', teamData.length, 'members');

                if (teamData.length > 0) {
                  // Filter team members based on user role and parking access
                  const filteredTeamData = this.filterTeamMembersByRole(teamData);

                  const teamMembers = filteredTeamData.map((member: ApiTeamMember) => this.mapApiMemberToTeamMember(member));

                  this.data.set(teamMembers);
                  this.filteredData.set(teamMembers);
                  // Enrich team members with device counts from our sessions API
                  this.enrichVisiblePageDeviceCounts();
                  // this.fetchParkingNames(teamMembers);
                } else {
                  console.warn('️ No team data, setting empty arrays');
                  this.data.set([]);
                  this.filteredData.set([]);
                }

                this.loading.set(false);
                this.skeleton = false;
                this.cdr.detectChanges();
              },
              error: (error: any) => {
                console.error('Error loading team members:', error);
                this.error.set('Failed to load team members');
                this.loading.set(false);
                this.skeleton = false;
                this.cdr.detectChanges();
              }
            })
          );
        },
        error: (error) => {
          // Continue with team loading even if roles fail
          this.loadTeamMembersWithParkingIdWithoutRoles(parkingId);
        }
      })
    );
  }

  private loadTeamMembersWithParkingIdWithoutRoles(parkingId: string) {
    console.log('Loading team members for parking without roles:', parkingId);

    // Use parking-specific API
    this.subscription.add(
      this.apiService.getParkingUsers(parkingId).subscribe({
        next: (response: any) => {
          // Handle different response formats
          let teamData: any[] = [];

          if (Array.isArray(response)) {
            teamData = response;
          } else if (response && response.users && Array.isArray(response.users)) {
            // Response with users array (parking users API format)
            teamData = response.users;
          } else if (response && response.data && Array.isArray(response.data)) {
            teamData = response.data;
          } else if (response && response.success && response.data && Array.isArray(response.data)) {
            teamData = response.data;
          } else {
            console.warn('️ Unexpected response format:', response);
            this.error.set('Invalid response format');
            this.loading.set(false);
            this.skeleton = false;
            this.cdr.detectChanges();
            return;
          }

          if (teamData.length > 0) {
            const teamMembers = teamData.map((member: ApiTeamMember) => this.mapApiMemberToTeamMember(member));
            this.data.set(teamMembers);
            this.filteredData.set(teamMembers);
            // Enrich team members with device counts from our sessions API
            this.enrichVisiblePageDeviceCounts();
            // this.fetchParkingNames(teamMembers);
          } else {
            this.data.set([]);
            this.filteredData.set([]);
          }

          this.loading.set(false);
          this.skeleton = false;
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error loading team members:', error);
          this.error.set('Failed to load team members');
          this.loading.set(false);
          this.skeleton = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  private loadTeamMembersWithoutRoles(orgId: string) {
    console.log('Loading team members without roles for organization:', orgId);

    // Use organization-wide API with a higher limit to fetch all members
    console.log('Using organization-wide API with org ID:', orgId);
    const apiCall = this.apiService.getAllOrganizationUsers(orgId);

    this.subscription.add(
      apiCall.subscribe({
        next: (response: any) => {
          // Handle different response formats
          let teamData: any[] = [];

          if (Array.isArray(response)) {
            // Direct array response (organization users API format)
            teamData = response;
          } else if (response && response.users && Array.isArray(response.users)) {
            // Response with users array (parking users API format)
            teamData = response.users;
          } else if (response && response.data && Array.isArray(response.data)) {
            // Wrapped in ApiResponse object
            teamData = response.data;
          } else if (response && response.success && response.data && Array.isArray(response.data)) {
            // Standard ApiResponse format
            teamData = response.data;
          } else {
            console.warn('️ Unexpected response format:', response);
            this.error.set('Invalid response format');
            this.loading.set(false);
            this.skeleton = false;
            this.cdr.detectChanges();
            return;
          }

          console.log('Team data received (no roles):', teamData.length, 'members');

          if (teamData.length > 0) {
            // For organization-wide view, we show all members without filtering by parking context
            const teamMembers = teamData.map((member: ApiTeamMember) => this.mapApiMemberToTeamMember(member));

            this.data.set(teamMembers);
            this.filteredData.set(teamMembers);
            // Enrich team members with device counts from our sessions API
            this.enrichVisiblePageDeviceCounts();
            // this.fetchParkingNames(teamMembers);
          } else {
            console.warn('️ No team data (no roles), setting empty arrays');
            this.data.set([]);
            this.filteredData.set([]);
          }

          this.loading.set(false);
          this.skeleton = false;
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error loading team members (no roles):', error);
          this.error.set('Failed to load team members');
          this.loading.set(false);
          this.skeleton = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  private mapApiMemberToTeamMember(apiMember: any): TeamMember {
    // Handle both old (firstName/lastName) and new (name) API formats
    let fullName: string;
    if (apiMember.name) {
      fullName = apiMember.name.trim();
    } else if (apiMember.firstName || apiMember.lastName) {
      fullName = `${apiMember.firstName || ''} ${apiMember.lastName || ''}`.trim();
    } else {
      fullName = '-';
    }
    const initials = this.getInitials(fullName);

    // Extract parking information from the new API structure
    let parkingId = null;
    let parkingName = null;

    if (apiMember.parkings && Array.isArray(apiMember.parkings) && apiMember.parkings.length > 0) {
      // Get the first non-null parking
      const firstParking = apiMember.parkings.find((p: any) => p != null);
      if (firstParking) {
        parkingId = firstParking.id;
        parkingName = firstParking.name;
      }
    } else if (apiMember.parkingId) {
      // Fallback to old structure
      parkingId = apiMember.parkingId;
    }

    // Handle string status ("active"/"inactive"/"suspend"/"suspended") or boolean status
    const normalizedStatus = String(apiMember.status || '').toLowerCase();
    const isActiveStatus = typeof apiMember.status === 'string'
      ? normalizedStatus === 'active'
      : Boolean(apiMember.status);

    // Get role from role.name or type field
    const roleString = apiMember.role?.name || apiMember.type || 'user';

    // Extract parkingIds array
    let parkingIds: string[] = [];
    if (apiMember.parkingIds && Array.isArray(apiMember.parkingIds) && apiMember.parkingIds.length > 0) {
      parkingIds = apiMember.parkingIds;
    } else if (apiMember.parkings && Array.isArray(apiMember.parkings) && apiMember.parkings.length > 0) {
      parkingIds = apiMember.parkings.filter((p: any) => p != null).map((p: any) => p.id);
    }

    // Check orgUsersCacheMap if missing
    if (parkingIds.length === 0) {
      const cached = this.orgUsersCacheMap.get(apiMember.id) || (apiMember.email ? this.orgUsersCacheMap.get(apiMember.email.toLowerCase()) : null);
      if (cached) {
        if (cached.parkingIds && Array.isArray(cached.parkingIds) && cached.parkingIds.length > 0) {
          parkingIds = cached.parkingIds;
        } else if (cached.parkings && Array.isArray(cached.parkings) && cached.parkings.length > 0) {
          parkingIds = cached.parkings.filter((p: any) => p != null).map((p: any) => p.id);
        }
      }
    }

    if (parkingIds.length === 0 && parkingId) {
      parkingIds = [parkingId];
    } else if (parkingIds.length === 0 && this.contextParkingId) {
      parkingId = this.contextParkingId;
      parkingIds = [this.contextParkingId];
    }

    const mappedMember: TeamMember = {
      id: apiMember.id,
      name: fullName,
      email: apiMember.email,
      role: this.mapUserTypeToRole(roleString, apiMember.role?.id || apiMember.role_id),
      role_id: apiMember.role?.id || apiMember.role_id,
      lastLogin: new Date(apiMember.last_login_at || apiMember.last_activity_at || apiMember.updatedAt || apiMember.createdAt),
      user_pov: apiMember.user_pov,
      devices: apiMember.active_sessions_count || apiMember.sessions_count ||
        (Array.isArray(apiMember.sessions) ? apiMember.sessions.length : 0) ||
        (Array.isArray(apiMember.active_sessions) ? apiMember.active_sessions.length : 0) ||
        0, // Use real count if available
      status: normalizedStatus === 'suspend' || normalizedStatus === 'suspended' ? 'Suspended' : isActiveStatus ? 'Active' : 'Inactive',
      actions: 'View',
      isActive: isActiveStatus,
      avatar: apiMember.profile_image || apiMember.profile || undefined,
      initials: initials,
      parkingId: parkingId,
      parkingIds: parkingIds,
      scopeId: parkingId || this.contextParkingId || apiMember.org_id || this.contextOrgId || this.apiService.getCurrentOrganizationId() || null,
      parkingName: parkingName, // Will be populated from API response or later if needed
      type: apiMember.type,
      phone: apiMember.phone || apiMember.phoneNumber,
      gender: apiMember.gender,
      service_access: apiMember.service_access || undefined
    };
    
    return mappedMember;
  }

  protected getInitials(name: string): string {
    const words = name.trim().split(' ').filter(word => word.length > 0);

    if (words.length === 0) return 'U';
    if (words.length === 1) return words[0].charAt(0).toUpperCase();

    // For 2 or more words, take first letter of first and last word
    const firstLetter = words[0].charAt(0).toUpperCase();
    const lastLetter = words[words.length - 1].charAt(0).toUpperCase();

    return firstLetter + lastLetter;
  }

  private fetchParkingNames(teamMembers: TeamMember[]): void {
    // Get unique parking IDs
    const uniqueParkingIds = [...new Set(teamMembers
      .filter(member => member.parkingId)
      .map(member => member.parkingId!))];

    if (uniqueParkingIds.length === 0) {
      return; // No parking IDs to fetch
    }

    // Create a map to store parking names
    const parkingNameMap = new Map<string, string>();
    let completedRequests = 0;

    uniqueParkingIds.forEach(parkingId => {
      this.apiService.getParkingDetail(parkingId).subscribe({
        next: (response) => {
          let parkingName = `Parking ${parkingId.slice(-8)}`; // Default fallback

          if (response && response.data && response.data.name) {
            parkingName = response.data.name;
          } else if (response && (response as any).name) {
            parkingName = (response as any).name;
          }

          parkingNameMap.set(parkingId, parkingName);
          completedRequests++;

          // If all requests are completed, update the team members
          if (completedRequests === uniqueParkingIds.length) {
            this.updateTeamMembersWithParkingNames(parkingNameMap, teamMembers);
          }
        },
        error: (error) => {
          console.error(`Error fetching parking ${parkingId}:`, error);
          parkingNameMap.set(parkingId, `Parking ${parkingId.slice(-8)}`);
          completedRequests++;

          // If all requests are completed, update the team members
          if (completedRequests === uniqueParkingIds.length) {
            this.updateTeamMembersWithParkingNames(parkingNameMap, teamMembers);
          }
        }
      });
    });
  }

  private updateTeamMembersWithParkingNames(parkingNameMap: Map<string, string>, teamMembers: TeamMember[]): void {
    const currentMembersMap = new Map(this.data().map(member => [member.id, member]));

    // Update team members with parking names
    const updatedTeamMembers = teamMembers.map(member => ({
      ...member,
      devices: currentMembersMap.get(member.id)?.devices ?? member.devices,
      parkingName: member.parkingId ? parkingNameMap.get(member.parkingId) || null : null
    }));

    // Update the data signals
    this.data.set(updatedTeamMembers);
    this.filteredData.set(this.getFilteredData());
    this.cdr.detectChanges();
  }

  private enrichVisiblePageDeviceCounts(): void {
    const dataList = this.filteredData();
    if (!dataList.length) {
      return;
    }

    const start = this.index * this.size;
    const end = (this.index + 1) * this.size;
    const visibleMembers = dataList.slice(start, end);

    this.enrichTeamMembersWithDeviceCounts(visibleMembers);
  }

  private enrichTeamMembersWithDeviceCounts(teamMembers: TeamMember[]): void {
    if (!teamMembers.length) {
      return;
    }

    const requests = teamMembers.map(member =>
      this.apiService.getUserSessions(member.id).pipe(
        map((response: any) => {
          const sessions = Array.isArray(response) ? response : (response?.data || []);
          return {
            userId: member.id,
            devices: Array.isArray(sessions) ? sessions.length : 0,
          };
        }),
        catchError((error) => {
          console.warn(`Failed to fetch sessions for user ${member.id}:`, error);
          return of({
            userId: member.id,
            devices: member.devices || 0,
          });
        })
      )
    );

    this.subscription.add(
      forkJoin(requests).subscribe((deviceCounts) => {
        const deviceCountMap = new Map(deviceCounts.map(entry => [entry.userId, entry.devices]));
        const updatedTeamMembers = this.data().map(member => ({
          ...member,
          devices: deviceCountMap.has(member.id) ? deviceCountMap.get(member.id) ?? member.devices : member.devices,
        }));

        this.data.set(updatedTeamMembers);
        this.filteredData.set(this.getFilteredData());
        this.cdr.detectChanges();
      })
    );
  }

  private mapUserTypeToRole(userType: string | null, roleId?: string): string {
    // First try to get role name from role_id using RoleService
    if (roleId) {
      const roleName = this.roleService.getRoleNameById(roleId);
      if (roleName && roleName !== roleId) {
        return roleName; // Return the actual role name from the service
      }
    }

    // Fallback to userType mapping if role_id doesn't work
    if (!userType) {
      return 'User'; // Default role for users without type
    }

    const roleMap: { [key: string]: string } = {
      'admin': 'Admin',
      'operator': 'Operator',
      'manager': 'Manager',
      'accountant': 'Accountant',
      'user': 'User',
      'viewer': 'Viewer',
      'vendor': 'Vendor'
    };
    return roleMap[userType.toLowerCase()] || 'User';
  }

  protected readonly filteredData = signal<TeamMember[]>([]);

  protected readonly Math = Math;

  protected readonly roleList = [
    {
      name: 'Admin',
      description: 'Full access to the entire organization - can manage parking lots, users, settings, billing, and create custom roles. Ideal for business owners or IT leads.',
      backgroundColor: 'var(--tui-primary)',
      iconSrc: 'assets/images/icons/modules/Users.svg'
    },
    {
      name: 'Manager',
      description: 'Manages one or more parking lots. Can view reports, configure facilities and pricing, and oversee operators. Best suited for local vendors or site-level supervisors.',
      backgroundColor: 'var(--tui-success)',
      iconSrc: 'assets/images/icons/modules/Users.svg'
    },
    {
      name: 'Operator',
      description: 'Day-to-day parking operator. Can log vehicle entries/exits, apply passes, and manage facility usage like valet or EV charging. No access to settings or billing.',
      backgroundColor: 'var(--tui-warning)',
      iconSrc: 'assets/images/icons/modules/Users.svg'
    },
    {
      name: 'Accountant',
      description: 'Can view and download transactions, monitor revenue reports, and track financial performance. No access to parking logs or team management.',
      backgroundColor: 'var(--tui-info)',
      iconSrc: 'assets/images/icons/modules/Users.svg'
    },
  ];

  protected getTeamRoles(): TeamRole[] {
    return this.teamRoles;
  }

  protected getTeamRoleBackgroundColor(roleName: string): string {
    const role = this.roleList.find(r => r.name === roleName);
    return role ? role.backgroundColor : 'var(--tui-base-03)';
  }

  protected getTeamRoleIconSrc(roleName: string): string {
    const role = this.roleList.find(r => r.name === roleName);
    return role ? role.iconSrc : 'assets/images/icons/modules/Users.svg';
  }

  protected addVehicleTypeFromButton(): void {
    // This method can be used to add new team members
  }

  private getFilteredData() {
    let filtered = this.data();

    // Apply search filter
    if (this.appliedFilters.search) {
      const searchTerm = this.appliedFilters.search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.name || '').toLowerCase().includes(searchTerm) ||
        (item.email || '').toLowerCase().includes(searchTerm) ||
        (item.role || '').toLowerCase().includes(searchTerm)
      );
    }

    // Apply status filter
    if (this.appliedFilters.status) {
      filtered = filtered.filter(item => item.status === this.appliedFilters.status);
    }

    // Apply role filter
    if (this.appliedFilters.role) {
      filtered = filtered.filter(item => item.role === this.appliedFilters.role);
    }

    return filtered;
  }

  protected applyFilters() {
    this.appliedFilters = {
      search: this.form.get('search')?.value || '',
      status: this.form.get('statusFilter')?.value || '',
      role: this.form.get('roleFilter')?.value || ''
    };

    const filtered = this.getFilteredData();
    this.filteredData.set(filtered);
    this.updateFilterCount();
    this.index = 0; // Reset to first page
    this.enrichVisiblePageDeviceCounts();
  }

  protected hasActiveFilters(): boolean {
    return Object.values(this.appliedFilters).some(value => value !== '');
  }

  protected getDropdownWidth(items: string[]): string {
    const maxLength = Math.max(...items.map(item => item.length));
    return `${Math.max(maxLength * 8, 120)}px`;
  }

  protected getDropdownWidthFor(key: string): string {
    switch (key) {
      case 'status':
        return this.getDropdownWidth(this.statusOptions);
      case 'role':
        return this.getDropdownWidth(this.roleOptions);
      default:
        return '120px';
    }
  }

  protected clearFilters() {
    this.form.patchValue({
      search: '',
      statusFilter: '',
      roleFilter: ''
    });

    this.appliedFilters = {
      search: '',
      status: '',
      role: ''
    };

    this.filteredData.set(this.data());
    this.updateFilterCount();
    this.index = 0; // Reset to first page
    this.enrichVisiblePageDeviceCounts();
  }

  protected exportData() {
    // Implement export functionality
  }

  protected isOpen(itemId: string): boolean {
    return this.openDropdowns.has(itemId);
  }

  protected toggleDropdown(itemId: string): void {
    if (this.openDropdowns.has(itemId)) {
      this.openDropdowns.delete(itemId);
    } else {
      this.openDropdowns.clear(); // Close other dropdowns
      this.openDropdowns.add(itemId);
    }
  }

  @ViewChild('drawerComponent') drawerComponent?: DrawerInviteTeamMember;
  private editingMember: TeamMember | null = null;

  protected onActionClick(action: string, item: TeamMember): void {

    switch (action) {
      case 'View':
        // Implement view functionality
        break;
      case 'Edit':
        this.editingMember = item;
        // Fetch fresh user data from API before opening drawer
        this.fetchAndOpenUserForEdit(item.id);
        break;
      case 'Activate':
        this.activateTeamMember(item);
        break;
      case 'Suspend':
        this.suspendTeamMember(item);
        break;
      case 'Remove':
        this.removeTeamMember(item);
        break;
      case 'ChangePassword':
        this.selectedMemberForPassword = item;
        this.changePasswordForm.reset();
        this.openChangePasswordDialog();
        break;
    }
  }

  protected getStatusActionLabel(item: TeamMember): 'Activate' | 'Suspend' {
    return item.status === 'Suspended' ? 'Activate' : 'Suspend';
  }

  protected isAdminMember(item: TeamMember): boolean {
    return item.role.trim().toLowerCase() === 'admin';
  }

  protected canEditMember(item: TeamMember): boolean {
    return item.status !== 'Suspended';
  }

  protected canChangeMemberStatus(item: TeamMember): boolean {
    return !this.isAdminMember(item);
  }

  protected canDeleteMember(item: TeamMember): boolean {
    return !this.isAdminMember(item);
  }

  protected hasMemberActions(item: TeamMember): boolean {
    return this.canEditMember(item) || this.canChangeMemberStatus(item) || this.canDeleteMember(item);
  }

  // Fetch fresh user data from API and open drawer for editing
  private fetchAndOpenUserForEdit(userId: string): void {
    if (!userId) {
      this.notificationService.showError('User ID not found', 3000);
      return;
    }

    // Fetch fresh user data from API
    this.apiService.getUserById(userId).subscribe({
      next: (response: any) => {
        let userData = null;

        // Handle response - check different possible response structures
        if (response.data) {
          userData = response.data;
        } else if (response) {
          userData = response;
        }

        if (userData && this.drawerComponent) {
          // Map API response to TeamMember format
          const mappedMember = this.mapApiMemberToTeamMember(userData);

          console.log('Fresh user data fetched for edit:', mappedMember);
          console.log('Service access:', mappedMember.service_access);

          // Open drawer with fresh data
          this.drawerComponent.openDrawerWithMember(mappedMember);
        } else {
          this.notificationService.showError('Failed to load user data', 3000);
        }
      },
      error: (error) => {
        console.error('Error fetching user data for edit:', error);
        this.notificationService.showError('Failed to load user data. Please try again.', 3000);
      }
    });
  }

  private openChangePasswordDialog(): void {
    if (!this.changePasswordDialog) return;

    this.dialogs.open<boolean>(this.changePasswordDialog, {
      size: 's',
      dismissible: true,
      label: 'Change Password'
    }).subscribe({
      next: (confirmed) => {
        if (confirmed && this.selectedMemberForPassword) {
          this.submitChangePassword();
        }
      }
    });
  }

  protected onPasswordSubmit(observer: any): void {
    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }
    observer.next(true);
    observer.complete();
  }

  protected generatePassword(): void {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
    let password = "";
    for (let i = 0, n = charset.length; i < length; ++i) {
      password += charset.charAt(Math.floor(Math.random() * n));
    }
    
    this.changePasswordForm.patchValue({
      newPassword: password
    });
    
    // Mark as dirty and touched to trigger validation and UI updates
    const control = this.changePasswordForm.get('newPassword');
    if (control) {
      control.markAsDirty();
      control.markAsTouched();
    }
    
    this.cdr.detectChanges();
  }

  private submitChangePassword(): void {
    if (!this.selectedMemberForPassword) return;

    const password = this.changePasswordForm.get('newPassword')?.value;
    const userId = this.selectedMemberForPassword.id;

    if (!password || !userId) return;

    // Use updateUser with just the password field
    this.apiService.updateUser({ id: userId, password }).subscribe({
      next: (res) => {
        this.notificationService.showSuccess(`Password updated successfully for ${this.selectedMemberForPassword?.name}!`, 3000);
      },
      error: (error) => {
        console.error('Error updating password:', error);
        this.notificationService.showError('Failed to update password', 3000);
      }
    });
  }

  // Delete team member with confirmation
  protected removeTeamMember(member: TeamMember): void {
    const userId = member.id;

    if (!userId) {
      this.notificationService.showError('User ID not found', 3000);
      return;
    }

    // Show Taiga UI confirmation dialog
    const data: TuiConfirmData = {
      content: `Are you sure you want to permanently remove "${member.name || member.email}"? This action cannot be undone.`,
      yes: 'Remove',
      no: 'Cancel',
      appearance: 'error',
    };

    this.subscription.add(
      this.dialogs
        .open<boolean>(TUI_CONFIRM, {
          label: 'Confirm Remove Team Member',
          size: 's',
          data,
        })
        .pipe(
          switchMap((confirmed) => {
            if (confirmed) {
              const currentUserPov = this.apiService.getUserPov();
              const payload: { user_id: string; org_id?: string; parking_id?: string } = {
                user_id: userId
              };

              if (currentUserPov === 'organization') {
                payload.org_id = this.apiService.getCurrentOrganizationId() || undefined;
                console.log('️ Removing user from organization team with payload:', payload);
              } else {
                payload.parking_id = member.parkingId || this.getCurrentParkingIdFromContext() || this.apiService.getCurrentParkingId() || undefined;
                console.log('️ Removing user from parking team with payload:', payload);
              }

              return this.apiService.removeFromTeam(payload);
            }
            return of(null);
          })
        )
        .subscribe({
          next: (res) => {
            if (res) {
              console.log('User removed successfully:', res);
              this.notificationService.showSuccess(`Team member "${member.name || member.email}" removed successfully!`, 3000);
              this.loadTeamMembers();
            }
          },
          error: (error) => {
            console.error('Error removing user:', error);
            this.notificationService.showError('Failed to remove team member', 3000);
          }
        })
    );
  }

  // Suspend team member with confirmation
  protected suspendTeamMember(member: TeamMember): void {
    const userId = member.id;
    const previousState = {
      isActive: member.isActive,
      status: member.status,
    };

    if (!userId) {
      this.notificationService.showError('User ID not found', 3000);
      return;
    }

    // Show Taiga UI confirmation dialog
    const data: TuiConfirmData = {
      content: `Are you sure you want to suspend "${member.name || member.email}"? This will deactivate the team member.`,
      yes: 'Suspend',
      no: 'Cancel',
      appearance: 'error',
    };

    this.dialogs
      .open<boolean>(TUI_CONFIRM, {
        label: 'Confirm Team Member Suspension',
        size: 's',
        data,
      })
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(null);
          }

          this.applyMemberStatus(member, 'suspend');
          this.cdr.detectChanges();

          return this.apiService.updateVendorUserStatus(this.buildVendorStatusPayload(member, 'suspend'));
        })
      )
      .subscribe({
        next: (response) => {
          if (response === null) {
            // User cancelled - do nothing
            return;
          }

          this.notificationService.showSuccess(
            `Team member "${member.name || member.email}" has been suspended successfully!`,
            3000
          );

          // Refresh list from server
          this.loadTeamMembers();
        },
        error: (error) => {
          console.error('Error suspending team member:', error);
          member.isActive = previousState.isActive;
          member.status = previousState.status;
          this.cdr.detectChanges();
          this.notificationService.showError('Failed to suspend team member', 3000);
        }
      });
  }

  protected activateTeamMember(member: TeamMember): void {
    const userId = member.id;
    const previousState = {
      isActive: member.isActive,
      status: member.status,
    };

    if (!userId) {
      this.notificationService.showError('User ID not found', 3000);
      return;
    }

    const data: TuiConfirmData = {
      content: `Are you sure you want to activate "${member.name || member.email}"? This will restore the team member's access.`,
      yes: 'Activate',
      no: 'Cancel',
    };

    this.dialogs
      .open<boolean>(TUI_CONFIRM, {
        label: 'Confirm Team Member Activation',
        size: 's',
        data,
      })
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(null);
          }

          this.applyMemberStatus(member, 'active');
          this.cdr.detectChanges();

          return this.apiService.updateVendorUserStatus(this.buildVendorStatusPayload(member, 'active'));
        })
      )
      .subscribe({
        next: (response) => {
          if (response === null) {
            return;
          }

          this.notificationService.showSuccess(
            `Team member "${member.name || member.email}" has been activated successfully!`,
            3000
          );

          this.loadTeamMembers();
        },
        error: (error) => {
          console.error('Error activating team member:', error);
          member.isActive = previousState.isActive;
          member.status = previousState.status;
          this.cdr.detectChanges();
          this.notificationService.showError('Failed to activate team member', 3000);
        }
      });
  }

  protected openInviteDrawer(): void {
    if (this.isUserLimitReached) {
      const max = this.maxUsersFromSubscription;
      this.notificationService.showError(
        `User limit reached. Your current plan allows a maximum of ${max} user${max === 1 ? '' : 's'}. Please upgrade your plan to add more team members.`,
        5000
      );
      return;
    }
    if (this.drawerComponent) {
      this.drawerComponent.openDrawer();
    }
  }

  protected onTeamMembersCreated(): void {
    // Refresh team data when new members are created
    this.loadTeamMembers();
  }

  protected refreshTeamData(): void {
    this.loadTeamMembers();
  }

  protected refreshRoleAndData(): void {
    this.roleService.forceRefreshRole();

    // Wait a bit for role to update, then load team members
    setTimeout(() => {
      this.loadTeamMembers();
    }, 100);
  }

  protected switchToVendorRole(): void {
    this.roleService.switchRole('vendor');
    this.refreshRoleAndData();
  }

  protected switchToAdminRole(): void {
    this.roleService.switchRole('admin');
    this.refreshRoleAndData();
  }





  protected getCurrentParkingIdFromContext(): string | null {
    const vendorContext = sessionStorage.getItem('vendorContext');
    if (vendorContext) {
      try {
        const context = JSON.parse(vendorContext);
        return context.parkingId || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  private setupAuthenticationMonitoring(): void {
    // Get initial user and org IDs
    const currentUser = this.apiService.getCurrentUser();
    this.currentUserId = currentUser?.id || null;
    this.currentOrgId = this.apiService.getCurrentOrganizationId();

    console.log('️ Monitoring started for user:', this.currentUserId, 'org:', this.currentOrgId);

    // Clean up all monitoring subscriptions on component destroy
    // Listen for authentication changes
    this.subscription.add(
      this.apiService.isAuthenticated$.pipe(
        distinctUntilChanged(),
        skip(1)
      ).subscribe(isAuthenticated => {
        console.log('Auth state changed:', isAuthenticated);
        if (!isAuthenticated) {
          this.data.set([]);
          this.filteredData.set([]);
          this.currentUserId = null;
          this.currentOrgId = null;
        } else {
          this.loadTeamMembers();
        }
      })
    );

    // Subscribe to role changes from RoleService
    // skip(1) avoids trigger on subscription (initial value) since ngOnInit handles start
    this.subscription.add(
      this.roleService.currentRole$.pipe(
        distinctUntilChanged(),
        skip(1)
      ).subscribe(newRole => {
        console.log('Role changed to:', newRole);
        this.loadTeamMembers();
      })
    );

    // Listen for storage events (cross-tab synchronization)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'user_info' || event.key === 'organization_id' || event.key === 'auth_token' || event.key === 'current_role') {
        const newUser = this.apiService.getCurrentUser();
        const newOrgId = this.apiService.getCurrentOrganizationId();

        if (newUser?.id !== this.currentUserId || newOrgId !== this.currentOrgId) {
          console.log('Cross-tab storage change detected, refreshing...');
          this.currentUserId = newUser?.id || null;
          this.currentOrgId = newOrgId;
          this.loadTeamMembers();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    this.subscription.add({
      unsubscribe: () => window.removeEventListener('storage', handleStorageChange)
    });
  }

  protected getSwitchSize(): TuiSizeS {
    return 's';
  }

  protected isAdminUser(): boolean {
    const currentUser = this.apiService.getCurrentUser();
    const currentRole = this.roleService.getCurrentRole();

    // Check if user is admin or super admin
    return currentRole?.toLowerCase() === 'admin' ||
      currentRole?.toLowerCase() === 'super admin' ||
      currentUser?.email === 'system_admin@kormoan.com';
  }

  protected getRoleDisplayText(): string {
    const used = this.data().length;

    // Limits and 'Seats Occupied' phrasing apply only in Parking POV
    if (this.isParkingPOV) {
      const max = this.maxUsersFromSubscription;
      if (max === -1) {
        return `${used} of Unlimited Seats Occupied`;
      }
      if (max && max > 0) {
        return `${used} of ${max} Seats Occupied`;
      }
      return `${used} ${used === 1 ? 'Seat Occupied' : 'Seats Occupied'}`;
    }

    // Organization POV: count ONLY organization-level members with clear phrasing
    const orgCount = this.getOrgLevelMemberCount();
    return `${orgCount} ${orgCount === 1 ? 'Organization Member' : 'Organization Members'}`;
  }

  /**
   * Count organization level members (members with organization scope / without specific parking assignment)
   */
  protected getOrgLevelMemberCount(): number {
    return this.data().filter(member =>
      member.scopeType === 'organization' ||
      member.user_pov === 'organization' ||
      (!member.parkingId && (!member.parkingIds || member.parkingIds.length === 0) && (!member.assignedParkingIds || member.assignedParkingIds.length === 0))
    ).length;
  }

  /**
   * Check if there are any organization level members
   */
  protected hasOrgLevelMembers(): boolean {
    return this.getOrgLevelMemberCount() > 0;
  }

  protected getParkingAssignmentCount(item: TeamMember): string {
    // 1. Organization level members have access to all parkings
    if (item.user_pov === 'organization' || (typeof item.role === 'string' && item.role.toLowerCase().includes('organization'))) {
      return 'All Parkings';
    }

    // 2. If user has explicit parking IDs array, show exact count
    if (item.parkingIds && item.parkingIds.length > 0) {
      return item.parkingIds.length === 1 ? '1 Parking' : `${item.parkingIds.length} Parkings`;
    }

    // 3. If user has a single parkingId, show 1 Parking
    if (item.parkingId) {
      return '1 Parking';
    }

    // 4. If viewing in parking context (Parking POV), default to 1 Parking for this parking
    if (this.contextParkingId) {
      return '1 Parking';
    }

    // 5. No specific parking assigned — user has access to all parkings
    return 'All Parkings';
  }

  /**
   * Get access type text for a team member (Parking or Organization Level)
   */
  protected getAccessTypeText(item: TeamMember): string {
    if (item.parkingId || item.user_pov === 'parking') {
      return 'Parking';
    }
    return 'Organization';
  }

  protected toggleMemberStatus(item: TeamMember, newValue?: boolean): void {
    const isActive = newValue !== undefined ? newValue : !item.isActive;
    const targetStatus = isActive ? 'active' : 'suspend';
    const previousState = {
      isActive: item.isActive,
      status: item.status,
    };

    this.applyMemberStatus(item, targetStatus);
    this.cdr.detectChanges();

    this.apiService.updateVendorUserStatus(this.buildVendorStatusPayload(item, targetStatus)).subscribe({
      next: () => {
        const statusText = isActive ? 'activated' : 'suspended';
        const notificationMessage = `${item.name} has been ${statusText} successfully`;

        if (isActive) {
          this.notificationService.showSuccess(notificationMessage, 3000);
        } else {
          this.notificationService.showWarning(notificationMessage, 3000);
        }

        this.loadTeamMembers();
      },
      error: (error) => {
        console.error('Error updating team member status:', error);
        item.isActive = previousState.isActive;
        item.status = previousState.status;
        this.cdr.detectChanges();
        this.notificationService.showError(`Failed to ${isActive ? 'activate' : 'suspend'} team member`, 3000);
      }
    });
  }

  private applyMemberStatus(member: TeamMember, status: 'active' | 'suspend'): void {
    member.isActive = status === 'active';
    member.status = status === 'active' ? 'Active' : 'Suspended';
  }

  private buildVendorStatusPayload(member: TeamMember, status: 'active' | 'suspend') {
    const scopeId =
      localStorage.getItem('organization_id') ||
      sessionStorage.getItem('organization_id') ||
      '';

    return {
      user_id: member.id,
      org_id: scopeId,
      status,
    } as const;
  }

  @ViewChild('devicesDrawer') devicesDrawer?: DevicesDrawer;

  @ViewChild('tooltipEl') tooltipEl?: ElementRef<HTMLElement>;

  // Add this method to open the modal
  public openDevicesModal(item: TeamMember): void {
    this.devicesDrawer?.openDrawer(item.id);
  }

  // Custom tooltip properties
  protected tooltipVisible = false;
  protected tooltipText = '';
  protected tooltipHeader = '';
  protected tooltipParkingList: any[] = [];
  protected tooltipPosition = { x: 0, y: 0 };
  private tooltipTriggerEl: HTMLElement | null = null;
  private tooltipMouseMoveListener: ((e: MouseEvent) => void) | null = null;

  /**
   * Show custom parking tooltip
   */
  protected showParkingTooltip(event: MouseEvent, item: TeamMember, showParkingList: boolean = true): void {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    const parkingInfo = this.getUserParkingInfo(item, showParkingList);
    this.tooltipHeader = parkingInfo.header;
    this.tooltipText = parkingInfo.description;
    this.tooltipParkingList = parkingInfo.parkingList;
    this.tooltipPosition = {
      x: rect.right + 12,
      y: rect.top + (rect.height / 2) - 26
    };
    this.tooltipVisible = true;
    this.tooltipTriggerEl = target;

    // Remove any existing listener before adding a new one
    this.removeTooltipMouseMoveListener();

    this.tooltipMouseMoveListener = (e: MouseEvent) => {
      const tooltipEl = this.tooltipEl?.nativeElement ?? null;
      const triggerEl = this.tooltipTriggerEl;

      const inTrigger = triggerEl ? this.isPointInRect(e.clientX, e.clientY, triggerEl.getBoundingClientRect(), 4) : false;
      const inTooltip = tooltipEl ? this.isPointInRect(e.clientX, e.clientY, tooltipEl.getBoundingClientRect(), 4) : false;

      if (!inTrigger && !inTooltip) {
        this.tooltipVisible = false;
        this.tooltipText = '';
        this.tooltipHeader = '';
        this.tooltipParkingList = [];
        this.tooltipTriggerEl = null;
        this.removeTooltipMouseMoveListener();
        this.cdr.detectChanges();
      }
    };

    document.addEventListener('mousemove', this.tooltipMouseMoveListener);
  }

  private isPointInRect(x: number, y: number, rect: DOMRect, padding = 0): boolean {
    return x >= rect.left - padding && x <= rect.right + padding &&
           y >= rect.top - padding && y <= rect.bottom + padding;
  }

  private removeTooltipMouseMoveListener(): void {
    if (this.tooltipMouseMoveListener) {
      document.removeEventListener('mousemove', this.tooltipMouseMoveListener);
      this.tooltipMouseMoveListener = null;
    }
  }

  /** @deprecated kept for template backward compat — logic now in mousemove */
  protected hideParkingTooltip(): void { }

  /** @deprecated kept for template backward compat */
  protected keepTooltipVisible(): void { }

  /**
   * Get parking information for a specific user
   * @param item - Team member
   * @param showParkingList - Whether to show parking list in tooltip (true for ACCESS column, false for ROLE column)
   */
  protected getUserParkingInfo(item: TeamMember, showParkingList: boolean = true): { header: string, description: string, parkingList: any[] } {
    const userParkings = this.apiService.getUserParkings();

    const resolveParkingName = (pid: string): string => {
      if (item.parkingId === pid && item.parkingName) return item.parkingName;
      const foundUser = userParkings?.find((p: any) => p.id === pid);
      if (foundUser && foundUser.name) return foundUser.name;
      const foundOrg = this.orgParkings?.find((p: any) => p.id === pid);
      if (foundOrg && foundOrg.name) return foundOrg.name;
      return `Parking ${pid.slice(-8)}`;
    };

    const isOrgMember = item.user_pov === 'organization' ||
      (typeof item.role === 'string' && item.role.toLowerCase().includes('organization')) ||
      (!item.parkingId && (!item.parkingIds || item.parkingIds.length === 0));

    // 1. Organization level members have access to all parkings
    if (isOrgMember && !this.contextParkingId) {
      const allParkings = this.orgParkings.length > 0 ? this.orgParkings : (this.contextOrgId ? [] : userParkings);
      const parkingList = (allParkings && allParkings.length > 0) ? allParkings.map((p: any) => ({
        id: p.id,
        name: p.name || `Parking ${p.id.slice(-8)}`
      })) : [];

      if (!showParkingList) {
        return {
          header: 'All Parkings Access',
          description: `User has organization-level access to all parking locations.`,
          parkingList: []
        };
      }
      return {
        header: 'All Parkings Access',
        description: 'User has access to all parking locations:',
        parkingList
      };
    }

    // 2. If user has explicit parking IDs array (1 or more)
    if (item.parkingIds && item.parkingIds.length > 0) {
      const parkingList = item.parkingIds.map((pid: string) => ({
        id: pid,
        name: resolveParkingName(pid)
      }));
      const count = item.parkingIds.length;

      if (!showParkingList) {
        return {
          header: count === 1 ? '1 Parking Access' : 'Parking Level Access',
          description: count === 1 ? `User has parking-level access to "${parkingList[0]?.name}".` : `User has parking-level access to ${count} parking locations.`,
          parkingList: []
        };
      }
      return {
        header: count === 1 ? '1 Parking Access' : `${count} Parkings Access`,
        description: count === 1 ? 'User has access to the following parking facility:' : 'User has access to the following parking facilities:',
        parkingList
      };
    }

    // 3. If user has a single parkingId
    if (item.parkingId) {
      const parkingName = resolveParkingName(item.parkingId);
      if (!showParkingList) {
        return {
          header: '1 Parking Access',
          description: `User has parking-level access to "${parkingName}".`,
          parkingList: []
        };
      }
      return {
        header: '1 Parking Access',
        description: 'User has access to the following parking facility:',
        parkingList: [{ id: item.parkingId, name: parkingName }]
      };
    }

    // 4. If in parking context (Parking POV)
    if (this.contextParkingId) {
      const pid = this.contextParkingId;
      const parkingName = resolveParkingName(pid);
      if (!showParkingList) {
        return {
          header: '1 Parking Access',
          description: `User has parking-level access to "${parkingName}".`,
          parkingList: []
        };
      }
      return {
        header: '1 Parking Access',
        description: 'User has access to the following parking facility:',
        parkingList: [{ id: pid, name: parkingName }]
      };
    }

    // 5. Fallback for all parkings
    const allParkings = this.orgParkings.length > 0 ? this.orgParkings : (this.contextOrgId ? [] : userParkings);
    if (allParkings && allParkings.length > 0) {
      const parkingList = allParkings.map((p: any) => ({
        id: p.id,
        name: p.name || `Parking ${p.id.slice(-8)}`
      }));
      if (!showParkingList) {
        return {
          header: 'All Parkings Access',
          description: `User has access to all ${allParkings.length} parking locations.`,
          parkingList: []
        };
      }
      return {
        header: 'All Parkings Access',
        description: 'User has access to all parking locations:',
        parkingList
      };
    }

    return {
      header: 'All Parkings Access',
      description: 'User has access to all parking locations.',
      parkingList: []
    };
  }

  /**
   * Get parking details by ID with proper name fetching
   */
  private getParkingDetailsById(parkingId: string): any {
    // First try to get from user parkings
    const userParkings = this.apiService.getUserParkings();
    if (userParkings) {
      const parking = userParkings.find((p: any) => p.id === parkingId);
      if (parking && parking.name) {
        return {
          id: parking.id,
          name: parking.name
        };
      }
    }

    // If not found in user parkings, try to get from team member data
    const teamMembers = this.data();
    for (const member of teamMembers) {
      if (member.parkingId === parkingId && member.parkingName) {
        return {
          id: parkingId,
          name: member.parkingName
        };
      }
    }

    // If still not found, make an API call to get parking details
    this.apiService.getParkingDetail(parkingId).subscribe({
      next: (response) => {
        let parkingName = `Parking ${parkingId.slice(-8)}`; // Default fallback

        if (response && response.data && response.data.name) {
          parkingName = response.data.name;
        } else if (response && (response as any).name) {
          parkingName = (response as any).name;
        }

        // Update the tooltip with the fetched name
        this.updateTooltipWithParkingName(parkingId, parkingName);
      },
      error: (error) => {
        // Keep the fallback name
      }
    });

    // Return fallback immediately while API call is in progress
    return {
      id: parkingId,
      name: `Parking ${parkingId.slice(-8)}`
    };
  }

  /**
   * Update tooltip with fetched parking name
   */
  private updateTooltipWithParkingName(parkingId: string, parkingName: string): void {
    if (this.tooltipVisible && this.tooltipParkingList.length > 0) {
      const updatedList = this.tooltipParkingList.map(parking => {
        if (parking.id === parkingId) {
          return { ...parking, name: parkingName };
        }
        return parking;
      });

      this.tooltipParkingList = updatedList;
      this.cdr.detectChanges();
    }
  }

  protected togglePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = !this.isPageSizeDropdownOpen;
    this.cdr.detectChanges();
  }

  protected onPageSizeChange(newSize: number): void {
    this.size = newSize;
    this.isPageSizeDropdownOpen = false;
    this.index = 0; // Reset to first page when changing page size
    this.enrichVisiblePageDeviceCounts();
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.removeTooltipMouseMoveListener();
  }

  /**
   * Filter team members based on current user's role and parking access
   */
  private filterTeamMembersByRole(teamData: any[]): any[] {
    const currentUser = this.apiService.getCurrentUser();
    const currentRole = this.roleService.getCurrentRole();

    console.log('filterTeamMembersByRole called:', {
      currentRole,
      currentUserType: currentUser?.type,
      teamDataLength: teamData.length,
      contextParkingId: this.contextParkingId
    });

    // If no context parking ID is set (e.g., viewing settings at organization level), return all members
    if (!this.contextParkingId) {
      console.log('No contextParkingId found, returning all organization members');
      return teamData;
    }

    // Filter to only show members "related" to this parking:
    // 1. Members explicitly assigned to this parking
    // 2. Members with NO specific parking assignment (Organization-level)
    console.log(`️ Filtering for specific parking context: ${this.contextParkingId}`);

    return teamData.filter((member: any) => {
      // Check if member is explicitly assigned to this parking context
      const isAssignedToThisParking =
        (member.parkingId && member.parkingId === this.contextParkingId) ||
        (Array.isArray(member.parkings) && member.parkings.some((p: any) => (p?.id || p) === this.contextParkingId)) ||
        (Array.isArray(member.parkingIds) && member.parkingIds.includes(this.contextParkingId));

      if (isAssignedToThisParking) return true;

      // Check if member is organization-level (has no specific parking assignment)
      // A member is considered organization-level if they have no parkingId and no parkings array
      const hasAnyParkingAssignment =
        (member.parkingId && member.parkingId.length > 0) ||
        (Array.isArray(member.parkings) && member.parkings.length > 0) ||
        (Array.isArray(member.parkingIds) && member.parkingIds.length > 0);

      // If they have NO parking assignment, they are Organization level and should stay visible
      if (!hasAnyParkingAssignment) return true;

      // Filter out members who are assigned to other parkings
      return false;
    });
  }
}
